import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RTooltip,
    ResponsiveContainer,
    Dot
} from 'recharts';
import { ArrowLeft } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api, fmtDate, fmtMoney, fmtQty } from '../lib/api';

export default function ItemDetailPage() {
    const [sp] = useSearchParams();
    const nav = useNavigate();
    const vendor_key = sp.get('vendor_key') || '';
    const item_key = sp.get('item_key') || '';
    const [data, setData] = useState(null);

    useEffect(() => {
        api.itemHistory(vendor_key, item_key).then(setData);
    }, [vendor_key, item_key]);

    const chartData = useMemo(
        () =>
            (data?.points || []).map((p, idx) => ({
                idx,
                label: p.bill_date || (p.uploaded_at || '').slice(0, 10),
                price: p.unit_price,
                bill_id: p.bill_id,
                vendor: p.vendor
            })),
        [data]
    );

    if (!data) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-8 w-40" />
                <Skeleton className="h-60" />
            </div>
        );
    }

    const first = data.points?.[0];
    const last = data.points?.[data.points.length - 1];

    return (
        <div className="space-y-5 animate-fade-in" data-testid="item-detail-page">
            <div className="flex items-center justify-between">
                <Button variant="ghost" onClick={() => nav('/items')} className="gap-2" data-testid="item-detail-back">
                    <ArrowLeft size={16} /> Back to items
                </Button>
            </div>

            <Card className="p-5 border-border/80">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <div className="font-heading text-2xl">{last?.item_name || first?.item_name || 'Item'}</div>
                        <div className="text-sm text-muted-foreground">{last?.vendor || first?.vendor}</div>
                    </div>
                    <div className="flex gap-4">
                        <div>
                            <div className="text-xs text-muted-foreground">Latest price</div>
                            <div className="font-heading text-xl tabular-nums">{fmtMoney(last?.unit_price)}</div>
                        </div>
                        <div>
                            <div className="text-xs text-muted-foreground">Data points</div>
                            <div className="font-heading text-xl tabular-nums">{data.points.length}</div>
                        </div>
                    </div>
                </div>
            </Card>

            <Card className="p-5 border-border/80">
                <div className="font-heading text-lg mb-3">Price history</div>
                {chartData.length < 1 ? (
                    <div className="text-sm text-muted-foreground">No price data yet.</div>
                ) : (
                    <div style={{ width: '100%', height: 260 }} data-testid="item-price-history-chart">
                        <ResponsiveContainer>
                            <LineChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 10 }}>
                                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                                <XAxis dataKey="label" fontSize={12} stroke="hsl(var(--muted-foreground))" />
                                <YAxis fontSize={12} stroke="hsl(var(--muted-foreground))" />
                                <RTooltip
                                    formatter={(v) => fmtMoney(v)}
                                    contentStyle={{ borderRadius: 8, borderColor: 'hsl(var(--border))' }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="price"
                                    stroke="hsl(var(--chart-1))"
                                    strokeWidth={2}
                                    dot={{ r: 4, stroke: 'hsl(var(--chart-1))', fill: 'white' }}
                                    activeDot={{ r: 6 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </Card>

            <Card className="p-0 border-border/80 overflow-hidden">
                <div className="px-5 py-3 font-heading text-lg border-b border-border">Bill-by-bill</div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="item-history-table">
                        <thead className="bg-muted/40 text-muted-foreground">
                            <tr>
                                <th className="text-left px-4 py-2 font-medium">Bill date</th>
                                <th className="text-left px-4 py-2 font-medium">Vendor</th>
                                <th className="text-right px-4 py-2 font-medium">Qty</th>
                                <th className="text-right px-4 py-2 font-medium">Unit price</th>
                                <th className="text-right px-4 py-2 font-medium">Bill</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.points.map((p, idx) => (
                                <tr key={idx} className="border-t border-border hover:bg-muted/40">
                                    <td className="px-4 py-2">{fmtDate(p.bill_date)}</td>
                                    <td className="px-4 py-2 text-muted-foreground">{p.vendor}</td>
                                    <td className="px-4 py-2 text-right tabular-nums font-mono">{fmtQty(p.quantity, { dash: '1' })}</td>
                                    <td className="px-4 py-2 text-right tabular-nums font-mono">{fmtMoney(p.unit_price)}</td>
                                    <td className="px-4 py-2 text-right">
                                        <Link to={`/bills/${p.bill_id}`} className="text-primary hover:underline text-xs">
                                            open
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}
